import React from 'react';
import { PermissionType } from '@watershed/constants/permissions';

import { GQNextLifecycleStage } from '@watershed/app-dashboard/generated/graphql-schema-types';

import { gql } from 'graphql-tag';
import TitleCard from '../components/home/TitleCard';
import WelcomeCard from '../components/home/WelcomeCard';
import KickoffCard from '../components/home/KickoffCard';
import NextMeasurementCard from '../components/home/NextMeasurementCard';
import NextReductionsCard from '../components/home/NextReductionsCard';

import {
  useHomePageQuery,
  useHomePageExpensiveQuery,
} from '@watershed/app-dashboard/generated/urql';
import isFetchingOrStale from '@watershed/shared-frontend/utils/isFetchingOrStale';
import PageContainer from '@watershed/shared-frontend/components/PageContainer';
import { useUserContext } from '../utils/UserContext';
import assertNever from '@watershed/shared-util/assertNever';
import flattenConnection from '@watershed/shared-universal/utils/flattenConnection';
import {
  getGqlResultDataBang,
  InvalidApiResponseError,
  maybeNotifySentry,
} from '@watershed/shared-frontend/utils/errorUtils';
import { ErrorBoundary } from 'react-error-boundary';
import useHasPermission from '../utils/useHasPermission';
import { Box, Stack, Fade } from '@mui/material';
import Skeleton from '@watershed/ui-core/components/Skeleton';

import { OnboardingHomePage } from '../components/home/OnboardingHome';
import { useOwner } from '@watershed/shared-frontend/hooks/useOwner';
import { Teams } from '@watershed/constants/teams';
import { AddTeamBanner } from '../components/home/AddTeamBanner';

gql`
  fragment PlanFieldsForHomepage on Plan {
    id
    name
    forecast {
      id
      interval
      customIntensityConfigs {
        ...CustomIntensityConfigFields
      }
    }
    targets {
      ...PlanTargetFields
    }
    carbonOffsets: reduxCarbonOffsets {
      id
      ...SimpleTimeseriesFields
    }
    carbonRemoval: reduxCarbonRemoval {
      id
      ...SimpleTimeseriesFields
    }
  }

  fragment FootprintAnalysisTimeSeriesDataFields on FootprintAnalysisTimeSeriesDataPoint {
    date
    value
  }

  query HomePage @withOwner(owner: EnterpriseFoundations) {
    organization {
      id
      shouldShowOnboarding
    }
  }

  query HomePageExpensive(
    $hasViewReductionsPermissions: Boolean!
    $hasManageAnyDatasourcePermissions: Boolean!
  ) @withOwner(owner: EnterpriseFoundations) {
    measurementProjectsRelevantToViewer(limit: 1, filters: { active: true }, sort: { createdAt: desc })
      @include(if: $hasManageAnyDatasourcePermissions) {
      ...NextMeasurementProjectData
    }
    organization {
      id
      lifecycle {
        nextStage
      }
    }
    committedPlans: plans(state: Committed)
      @include(if: $hasViewReductionsPermissions) {
      edges {
        node {
          # TODO(EUR-1665)

          ...PlanFieldsForHomepage
        }
      }
    }
    visiblePlans: plans(state: Draft, first: 3)
      @include(if: $hasViewReductionsPermissions) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

function HomePageContent() {
  const { userName, orgName } = useUserContext();
  const hasAdmin = useHasPermission([PermissionType.Admin]);
  const canManageAnyDatasource = useHasPermission(
    [PermissionType.ManageDatasource],
    { allowAnyObject: true }
  );
  const canViewAnyReductionPlan = useHasPermission(
    [PermissionType.ViewReductions],
    { allowAnyObject: true }
  );

  const [result] = useHomePageExpensiveQuery({
    variables: {
      hasViewReductionsPermissions: canViewAnyReductionPlan,
      hasManageAnyDatasourcePermissions: canManageAnyDatasource,
    },
  });

  if (isFetchingOrStale(result)) {
    return <HomePageSkeletons />;
  }
  const data = getGqlResultDataBang(result);

  const lifecycle = data.organization?.lifecycle;
  InvalidApiResponseError.invariant(lifecycle);

  const plans: Array<{ id: string; name: string }> | null =
    canViewAnyReductionPlan ? flattenConnection(data.visiblePlans) : null;
  const committedPlans = canViewAnyReductionPlan
    ? flattenConnection(data.committedPlans)
    : null;
  InvalidApiResponseError.invariant(
    !canViewAnyReductionPlan || (committedPlans && committedPlans.length <= 1),
    'Expected at most one committed plan'
  );
  const nextComponent = (() => {
    if (lifecycle.nextStage === null) {
      return null;
    }

    switch (lifecycle.nextStage) {
      case GQNextLifecycleStage.Measurement:
        return canManageAnyDatasource ? (
          <Stack gap={2}>
            <NextMeasurementCard
              project={data.measurementProjectsRelevantToViewer?.[0]}
            />
          </Stack>
        ) : null;
      case GQNextLifecycleStage.Reductions:
        return canViewAnyReductionPlan ? (
          <>
            <NextReductionsCard plans={plans ?? []} />
          </>
        ) : null;
      case GQNextLifecycleStage.Reporting:
        return null;
      default:
        assertNever(lifecycle.nextStage);
    }
  })();

  return (
    <Fade in>
      <Stack gap={5}>
        <WelcomeCard />
        {nextComponent}
        {hasAdmin && (
          <AddTeamBanner
            // Would be good to populate with actual org collborators,
            // but leaving it with company and current user
            // as less expensive + no permissions required + purely decorative.
            userIds={[`${userName}`, `${orgName}`]}
          />
        )}
        <KickoffCard />
      </Stack>
    </Fade>
  );
}

function HomePageShell({ children }: React.PropsWithChildren) {
  const { orgName } = useUserContext();
  return (
    <Fade in>
      <Box sx={{ backgroundColor: (theme) => theme.palette.grey05 }}>
        <TitleCard orgName={orgName} />
        <Stack
          sx={{
            width: {
              sm: '100%', // theme.breakpoints.up('sm')
              md: '960px', // theme.breakpoints.up('md')
            },
            px: {
              sm: 2,
              md: 2,
              lg: 0,
            },
            minHeight: '100vh',
          }}
          pt={5}
          pb={5}
          margin="0 auto"
        >
          {children}
        </Stack>
      </Box>
    </Fade>
  );
}

function HomePageSkeletons() {
  return (
    <Stack gap={5}>
      <Skeleton
        sx={{ bgcolor: (theme) => theme.palette.grey20 }}
        variant="rounded"
        width="100%"
        height={138}
      />
      <Skeleton
        sx={{ bgcolor: (theme) => theme.palette.grey20 }}
        variant="rounded"
        width="100%"
        height={300}
      />
      <Skeleton
        sx={{ bgcolor: (theme) => theme.palette.grey20 }}
        variant="rounded"
        width="100%"
        height={54}
      />
      <Skeleton
        sx={{ bgcolor: (theme) => theme.palette.grey20 }}
        variant="rounded"
        width="100%"
        height={345}
      />
    </Stack>
  );
}

export default function HomePage() {
  useOwner(Teams.EnterpriseFoundations);

  const [result] = useHomePageQuery();
  if (isFetchingOrStale(result)) {
    return null;
  }
  const data = getGqlResultDataBang(result);

  const showOnboardingHomePage = data.organization.shouldShowOnboarding;

  return (
    <PageContainer isFullPage>
      <ErrorBoundary
        fallbackRender={() => null}
        onError={(error) => {
          console.error(
            'ERROR ON THE HOMEPAGE. We caught it because we would rather not break the homepage'
          );
          console.error(error);
          maybeNotifySentry(error);
        }}
      >
        {showOnboardingHomePage ? (
          <OnboardingHomePage />
        ) : (
          <HomePageShell>
            <HomePageContent />
          </HomePageShell>
        )}
      </ErrorBoundary>
    </PageContainer>
  );
}
